import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ArrowDownWideNarrow, ImageIcon, Layers, Play } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, productImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALLOC_STATUS_META = {
  "UNALLOCATED": "bg-stone-100 text-stone-600 border-stone-200",
  "PARTIAL": "bg-amber-50 text-amber-700 border-amber-200",
  "FULLY ALLOCATED": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function Stat({ label, value, testId, tone = "text-stone-800" }) {
  return (
    <div className="rounded-md border bg-[#EDE4DE] py-2 text-center">
      <p data-testid={testId} className={`font-heading text-xl font-extrabold ${tone}`}>{value}</p>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}

export default function Allocations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const canAccess = ["owner", "admin", "admin_input", "viewer"].includes(user.role);
  const canRun = ["owner", "admin", "admin_input"].includes(user.role);

  useEffect(() => {
    if (!canAccess) return;
    api.get("/batches").then((r) => {
      setBatches(r.data);
      if (r.data.length && !batchId) setBatchId(r.data[0].id);
    }).catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess]);

  const load = useCallback(async () => {
    if (!batchId) return;
    try {
      const { data } = await api.get("/allocations", { params: { po_batch_id: batchId } });
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
      setData(null);
    }
  }, [batchId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!canAccess) {
    return (
      <div data-testid="allocations-forbidden" className="grid place-items-center rounded-md border border-dashed py-20">
        <p className="font-medium text-muted-foreground">FIFO Allocation is only available to the Owner and Admin Checker.</p>
      </div>
    );
  }

  const runAllocation = async () => {
    setRunning(true);
    try {
      const { data: res } = await api.post("/allocations/run", { po_batch_id: batchId });
      if (res.allocated_events === 0) {
        toast.info("Nothing new to allocate — all demand is already covered by allocated stock");
      } else {
        toast.success(`Allocated ${res.allocated_events} item${res.allocated_events === 1 ? "" : "s"} in FIFO order`);
      }
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="allocations-page">
      <div className="mb-5">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">FIFO Allocation</h1>
        <p className="text-sm text-muted-foreground">
          Allocate checker-approved received stock to customer orders — oldest order item first (by sequence)
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Select value={batchId} onValueChange={setBatchId}>
            <SelectTrigger data-testid="allocation-batch-select" className="h-12 w-full sm:w-72">
              <SelectValue placeholder="Select PO Batch" />
            </SelectTrigger>
            <SelectContent>
              {batches.map((b) => (
                <SelectItem key={b.id} value={b.id} data-testid={`allocation-batch-${b.id}`}>
                  {b.name}{b.status === "closed" ? " (CLOSED)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {batchId && canRun && (
            <Button data-testid="run-allocation-button" className="h-12 px-5" onClick={runAllocation} disabled={running}>
              <Play className="h-4 w-4" /> {running ? "Allocating…" : "Run FIFO Allocation"}
            </Button>
          )}
        </div>
      </div>

      {!data ? (
        <div className="grid place-items-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : data.products.length === 0 ? (
        <div className="grid place-items-center rounded-md border border-dashed py-20 text-center">
          <p className="font-medium text-muted-foreground">No products in this batch</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="allocation-products">
          {data.products.map((p) => (
            <div key={p.product_id} data-testid={`allocation-product-${p.product_id}`} className="rounded-md border bg-card p-4">
              <div className="mb-3 flex items-center gap-3">
                {productImage(p) ? (
                  <img src={productImage(p)} alt={p.product_name} className="h-12 w-12 shrink-0 rounded-md border bg-stone-100 object-cover" />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border bg-stone-100">
                    <ImageIcon className="h-5 w-5 text-stone-300" />
                  </div>
                )}
                <p className="min-w-0 truncate font-heading text-lg font-bold tracking-tight">{p.product_name}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat label="Approved Received" value={p.approved_received} testId={`alloc-approved-${p.product_id}`} />
                <Stat label="Allocated" value={p.allocated} testId={`alloc-allocated-${p.product_id}`} />
                <Stat label="Available to Allocate" value={p.available_to_allocate} testId={`alloc-available-${p.product_id}`} tone="text-emerald-600" />
                <Stat label="Demand Remaining" value={p.demand_remaining} testId={`alloc-demand-${p.product_id}`} tone={p.demand_remaining > 0 ? "text-amber-600" : "text-stone-800"} />
              </div>
              {p.allocations.length === 0 ? (
                <p className="mt-3 rounded-md border border-dashed py-4 text-center text-xs text-muted-foreground">No customer demand for this product yet</p>
              ) : (
                <div className="mt-3 space-y-1.5" data-testid={`alloc-rows-${p.product_id}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    <ArrowDownWideNarrow className="mr-1 inline h-3 w-3" /> Customer demand in FIFO order
                  </p>
                  {p.allocations.map((r) => (
                    <button
                      key={r.order_item_id}
                      data-testid={`alloc-row-${r.order_item_id}`}
                      onClick={() => navigate(`/orders/${r.order_id}`)}
                      className="flex w-full items-center gap-3 rounded-md border bg-[#EDE4DE] px-3 py-2.5 text-left active:scale-[0.99]"
                    >
                      <span className="shrink-0 rounded-md bg-white px-2 py-0.5 font-heading text-xs font-extrabold text-primary" data-testid={`alloc-seq-${r.order_item_id}`}>
                        #{r.seq}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold">{r.customer_name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          ordered {format(new Date(r.item_created_at), "d MMM, HH:mm")}
                          {r.variant && ` · ${r.variant}`}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs font-semibold text-stone-600" data-testid={`alloc-qty-${r.order_item_id}`}>
                        {r.allocated_qty}/{r.ordered_qty}
                      </span>
                      <span className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-bold ${ALLOC_STATUS_META[r.status]}`} data-testid={`alloc-status-${r.order_item_id}`}>
                        {r.status}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {data && data.ledger.length > 0 && (
        <div className="mt-6" data-testid="allocation-ledger">
          <h2 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">
            <Layers className="mr-1 inline h-4 w-4" /> Allocation History
          </h2>
          <div className="space-y-1.5">
            {data.ledger.map((l) => (
              <div key={l.id} data-testid={`ledger-entry-${l.id}`} className="rounded-md border bg-card p-3">
                <p className="text-xs font-semibold">
                  {l.customer_name} — {l.product_name}: +{l.allocated_qty} allocated
                  {l.remaining_qty > 0 ? ` (${l.remaining_qty} of ${l.ordered_qty} still remaining)` : ` (item fully covered, ${l.ordered_qty}/${l.ordered_qty})`}
                  {l.released_at && (
                    <span className="ml-1 rounded-md border border-red-200 bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-600">
                      RELEASED (item cancelled)
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  item seq #{l.item_seq} · ordered {format(new Date(l.item_created_at), "d MMM, HH:mm")} · allocated {format(new Date(l.allocated_at), "d MMM yyyy, HH:mm")} by {l.allocated_by_name}
                  {l.released_at && ` · released ${format(new Date(l.released_at), "d MMM yyyy, HH:mm")} by ${l.released_by_name}`}
                  {l.source_receiving_session_ids?.length > 0 && ` · from ${l.source_receiving_session_ids.length} approved session${l.source_receiving_session_ids.length === 1 ? "" : "s"}`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
